import React, { useState } from 'react';

interface JsonTreeNodeProps {
  nodeKey?: string;
  value: any;
  depth?: number;
  defaultExpanded?: boolean;
}

export const JsonTreeNode: React.FC<JsonTreeNodeProps> = ({
  nodeKey,
  value,
  depth = 0,
  defaultExpanded = depth < 2,
}) => {
  const [open, setOpen] = useState(defaultExpanded);
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  const indent = depth * 16;

  const renderScalar = (v: any) => {
    if (v === null) return <span className="text-gray-400 italic">null</span>;
    if (typeof v === 'boolean') return <span className={v ? 'text-green-600' : 'text-red-500'}>{String(v)}</span>;
    if (typeof v === 'number') return <span className="text-orange-500">{String(v)}</span>;
    if (typeof v === 'string') return <span className="text-emerald-700 break-all">"{v}"</span>;
    return <span className="text-gray-700">{String(v)}</span>;
  };

  if (!isObject) {
    return (
      <div className="flex items-start gap-1 py-0.5 leading-5" style={{ paddingLeft: indent }}>
        {nodeKey !== undefined && (
          <>
            <span className="text-blue-700 font-medium shrink-0">"{nodeKey}"</span>
            <span className="text-gray-400 shrink-0">: </span>
          </>
        )}
        <span className="text-xs font-mono">{renderScalar(value)}</span>
      </div>
    );
  }

  const entries: [string, any][] = isArray
    ? value.map((v: any, i: number) => [String(i), v])
    : Object.entries(value);
  const bracketOpen = isArray ? '[' : '{';
  const bracketClose = isArray ? ']' : '}';
  const summary = isArray ? `${value.length} items` : `${entries.length} keys`;

  return (
    <div style={{ paddingLeft: nodeKey !== undefined ? indent : 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 py-0.5 hover:bg-blue-50 rounded px-1 w-full text-left text-xs font-mono"
      >
        <span className="text-gray-400 w-3 shrink-0">{open ? '▾' : '▸'}</span>
        {nodeKey !== undefined && (
          <>
            <span className="text-blue-700 font-medium">"{nodeKey}"</span>
            <span className="text-gray-400">: </span>
          </>
        )}
        <span className="text-gray-500">{bracketOpen}</span>
        {!open && <span className="text-gray-400 italic ml-1">{summary}</span>}
        {!open && <span className="text-gray-500">{bracketClose}</span>}
      </button>
      {open && (
        <div className="border-l border-gray-200 ml-1.5">
          {entries.map(([k, v]) => (
            <JsonTreeNode
              key={k}
              nodeKey={isArray ? undefined : k}
              value={v}
              depth={depth + 1}
              defaultExpanded={false}
            />
          ))}
          <div className="py-0.5 text-xs font-mono text-gray-500" style={{ paddingLeft: 4 }}>
            {bracketClose}
          </div>
        </div>
      )}
    </div>
  );
};

interface JsonTreeViewerProps {
  data: Record<string, any>;
  /** max-height tailwind class, defaults to max-h-72 */
  maxHeightClass?: string;
}

const JsonTreeViewer: React.FC<JsonTreeViewerProps> = ({ data, maxHeightClass = 'max-h-72' }) => {
  return (
    <div className={`${maxHeightClass} overflow-y-auto text-xs font-mono p-3`}>
      {Object.entries(data).map(([k, v]) => (
        <JsonTreeNode key={k} nodeKey={k} value={v} depth={0} defaultExpanded={false} />
      ))}
    </div>
  );
};

export default JsonTreeViewer;
